/**
 * TRADING — docs/03 §3.5.
 *
 * **`POST /orders` is the only place in this client where an HTTP response
 * blocks UI.** Everything else renders optimistically or from a stream. The
 * ticket's button resolves on the POST receipt, not on the WS `orders` frame,
 * because the receipt has a status code and the frame is a notification.
 *
 * Three anti-duplication mechanisms, three different jobs, and a correct client
 * uses all three (§6.3): `Idempotency-Key` stops one HTTP request executing
 * twice, `client_order_id` stops two logically different orders colliding on one
 * identity, and `X-UD-Nonce` stops a captured signature being replayed into
 * different market conditions. One logical order generates all three together
 * and reuses all three on every retry of that same order.
 *
 * `placeOrder` therefore **requires** its idempotency key rather than
 * generating one. A generated key is right for a first attempt and wrong for a
 * retry, and the retry is the case that places the duplicate.
 *
 * **Cancels never require the session key** and are never throttled with
 * placements: a cancel can only reduce risk (D3 scope), and a user whose key
 * expired mid-market must still be able to pull their orders (register §D8).
 */

import type { HttpClient, MutationOptions, RequestOptions, SessionKeySignature } from '../client'
import type { OkBody, Page, OpBody, OpQuery } from '../types'

export interface SignedOrderOptions extends RequestOptions {
  /** One logical order = one key, reused on every retry of that order. */
  idempotencyKey: string
  /** `X-UD-Key` / `X-UD-Signature` / `X-UD-Nonce` from the session key (§3.4.2). */
  signature: SessionKeySignature
}

export function tradingEndpoints(client: HttpClient) {
  return {
    placeOrder(body: OpBody<'placeOrder'>, options: SignedOrderOptions) {
      return client.post<OkBody<'placeOrder'>>('/orders', body, options)
    },

    /** Not used by the v1 web ticket. Reserved for the protocol market maker and
     *  the v1.1 "replace ladder" action; up to 20 orders, partial success per
     *  item (§4.2). Listed so the surface is complete, not because it is wired in. */
    placeOrderBatch(body: OpBody<'placeOrderBatch'>, options: SignedOrderOptions) {
      return client.post<OkBody<'placeOrderBatch'>>('/orders/batch', body, options)
    },

    /** Cold load; gap recovery on the `orders` channel; reconnect
     *  reconciliation. Also the way to resolve a lost POST response when the
     *  client never got an `order_id` back — hence `client_order_id` in the
     *  filter set, and hence it being mandatory on every order (§9.2.9). */
    listOrders(query?: OpQuery<'listOrders'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listOrders'>>>('/orders', { ...options, query })
    },

    /** The order-detail drawer, and the canonical way to resolve an in-flight
     *  POST when the client holds an `order_id` but no terminal state. */
    getOrder(orderId: string, options?: RequestOptions) {
      return client.get<OkBody<'getOrder'>>(`/orders/${encodeURIComponent(orderId)}`, options)
    },

    cancelOrder(orderId: string, options?: MutationOptions) {
      return client.delete<OkBody<'cancelOrder'>>(`/orders/${encodeURIComponent(orderId)}`, options)
    },

    /** "Cancel all", and the one-click action offered in the `MARKET_NOT_OPEN`
     *  toast. Not fired automatically at expiry — the engine already cancels the
     *  book (§6.2). Partial success arrives as `200` with `not_cancelled{}`. */
    cancelOrders(query: OpQuery<'cancelOrders'>, options?: MutationOptions) {
      return client.delete<OkBody<'cancelOrders'>>('/orders', { ...options, query })
    },

    /** Fills tab; gap recovery on the `fills` channel, then with `since_ms`. */
    listFills(query?: OpQuery<'listFills'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listFills'>>>('/fills', { ...options, query })
    },
  }
}

export type TradingEndpoints = ReturnType<typeof tradingEndpoints>
